import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { unstable_batchedUpdates } from "react-dom";
import type { RoleName } from "common/authorization";
import { arePathsEqual, type PathArray } from "common/utils/formStructure";
import { AuthContext } from "../../../../Authorization/AuthContext";
import type { ExistingSubmissionType } from "./types";

export type CanCreateGroup = {
  id: string;
  name: string;
  memberType: string;
};

export enum SUBMISSIONS_BUILDER_MODE {
  BUILD = "BUILD",
  VERSION_HISTORY = "VERSION_HISTORY",
}

export interface SubmissionsBuilderContextValue {
  existingSubmissionType: Maybe<ExistingSubmissionType> | undefined;
  selectedVersionId: string | undefined;
  selectVersion: (versionId: string | undefined) => void;
  canCreateRoles: Array<RoleName>;
  setCanCreateRoles: (roles: Array<RoleName>) => void;
  canCreateGroups: Array<CanCreateGroup>;
  setCanCreateGroups: (groups: Array<CanCreateGroup>) => void;
  mode: SUBMISSIONS_BUILDER_MODE;
  selectedField: PathArray | undefined;
  selectField: (fieldPath: PathArray | undefined) => void;
}

const SubmissionsBuilderContext = createContext<
  SubmissionsBuilderContextValue | undefined
>(undefined);

type SubmissionsBuilderContextProviderProps = PropsWithChildren<{
  existingSubmissionType?: Maybe<ExistingSubmissionType>;
}>;

export function SubmissionsBuilderContextProvider({
  existingSubmissionType,
  children,
}: SubmissionsBuilderContextProviderProps) {
  const { account } = useContext(AuthContext);

  const accountUserGroups = account?.userGroups ?? [];
  const initialCanCreateGroups = accountUserGroups.filter(userGroup =>
    existingSubmissionType?.canCreateGroups.find(
      canCreateGroup => canCreateGroup.id === userGroup.id,
    ),
  );
  const [canCreateGroups, setCanCreateGroups] = useState<Array<CanCreateGroup>>(
    initialCanCreateGroups,
  );

  const [canCreateRoles, setCanCreateRoles] = useState<Array<RoleName>>(
    existingSubmissionType?.canCreate ?? [],
  );

  const [selectedVersionId, setSelectedVersionId] = useState<
    string | undefined
  >();

  const selectVersion = useCallback((versionId: string | undefined) => {
    unstable_batchedUpdates(() => {
      setSelectedField(undefined);
      setSelectedVersionId(versionId);
    });
  }, []);

  const mode =
    selectedVersionId ?
      SUBMISSIONS_BUILDER_MODE.VERSION_HISTORY
    : SUBMISSIONS_BUILDER_MODE.BUILD;

  const [selectedField, setSelectedField] = useState<PathArray | undefined>();

  const value: SubmissionsBuilderContextValue = useMemo(
    () => ({
      existingSubmissionType,
      selectedVersionId,
      selectVersion,
      canCreateRoles,
      setCanCreateRoles,
      canCreateGroups,
      setCanCreateGroups,
      mode,
      selectedField,
      selectField: setSelectedField,
    }),
    [
      canCreateGroups,
      canCreateRoles,
      existingSubmissionType,
      mode,
      selectedField,
      selectedVersionId,
      selectVersion,
    ],
  );

  return (
    <SubmissionsBuilderContext.Provider value={value}>
      {children}
    </SubmissionsBuilderContext.Provider>
  );
}

export function useSubmissionsBuilderContext() {
  const context = useContext(SubmissionsBuilderContext);

  if (!context) {
    throw new Error(
      "useSubmissionsBuilderContext must be used within a SubmissionsBuilderContextProvider",
    );
  }
  return context;
}

export function useSubmissionsBuilderMode() {
  const { mode, selectVersion, existingSubmissionType } =
    useSubmissionsBuilderContext();

  const setMode = useCallback(
    (newMode: SUBMISSIONS_BUILDER_MODE) => {
      if (newMode === SUBMISSIONS_BUILDER_MODE.VERSION_HISTORY) {
        selectVersion(existingSubmissionType?.currentVersion?.id);
      } else {
        selectVersion(undefined);
      }
    },
    [selectVersion, existingSubmissionType?.currentVersion?.id],
  );

  return [mode, setMode] as const;
}

export function isFieldSelected(
  currentPathArray: PathArray,
  selectedPathArray: PathArray | undefined,
) {
  return (
    !!selectedPathArray && arePathsEqual(currentPathArray, selectedPathArray)
  );
}
